import { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import DemoSlideshow from '../components/DemoSlideshow';
import { AuthContext } from '../App';
import { useAuthForms } from '../hooks/useAuthForms';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import EmailInput from '../components/EmailInput';
import { Label } from '../components/ui/label';
import { AlertCircle, Eye, EyeOff, Heart } from 'lucide-react';

const VerifyForm = ({ verifyEmail, verifyCode, setVerifyCode, loading, handleVerify, handleResend, setView }) => (
  <Card className="card-surface border-[#27272A]" data-testid="verify-email-card">
    <CardHeader>
      <CardTitle className="text-2xl" style={{ fontFamily: 'Outfit' }}>Verify Your Email</CardTitle>
      <CardDescription>Enter the 6-digit code sent to <span className="text-[#F9FAFB] font-semibold">{verifyEmail}</span></CardDescription>
    </CardHeader>
    <CardContent>
      <form onSubmit={handleVerify} className="space-y-4">
        <div>
          <Label htmlFor="verificationCode">Verification Code</Label>
          <Input id="verificationCode" inputMode="numeric" pattern="[0-9]*" autoComplete="one-time-code" value={verifyCode} onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" maxLength={6} required className="bg-[#0A0A0A] border-[#27272A] focus:ring-[#8B5CF6] text-center tracking-[0.5em] text-xl" data-testid="verification-code-input" />
        </div>
        <Button type="submit" disabled={loading} className="btn-purple w-full rounded-full" data-testid="verify-email-button">{loading ? 'Verifying...' : 'Verify Email'}</Button>
        <div className="rounded-xl border border-[#27272A] bg-[#0A0A0A] p-3 text-xs text-[#A1A1AA] space-y-2">
          <p><span className="text-[#D4AF37] font-semibold">Didn't get it?</span> Check your <strong>spam / junk folder</strong>. Gmail sometimes routes new senders there.</p>
          <Button type="button" variant="ghost" onClick={() => handleResend()} className="w-full text-[#8B5CF6] hover:text-[#9D6FFF] h-9" data-testid="resend-verification-button">Resend code</Button>
        </div>
        <Button type="button" variant="ghost" onClick={() => setView('login')} className="w-full text-[#A1A1AA]" data-testid="back-to-login-from-verify">Back to Login</Button>
      </form>
    </CardContent>
  </Card>
);

const ForgotForm = ({ email, setEmail, handleForgot, setView }) => (
  <Card className="card-surface border-[#27272A]" data-testid="forgot-password-card">
    <CardHeader>
      <CardTitle className="text-2xl" style={{ fontFamily: 'Outfit' }}>Forget something?</CardTitle>
      <CardDescription>Enter your email to receive a reset token</CardDescription>
    </CardHeader>
    <CardContent>
      <form onSubmit={handleForgot} className="space-y-4">
        <div>
          <Label htmlFor="forgotEmail">Email</Label>
          <EmailInput id="forgotEmail" value={email} onChange={(e) => setEmail(e.target.value)} dataTestId="forgot-email-input" />
        </div>
        <Button type="submit" className="btn-purple w-full rounded-full" data-testid="send-reset-button">Send Reset Token</Button>
        <Button type="button" variant="ghost" onClick={() => setView('login')} className="w-full text-[#A1A1AA]" data-testid="back-to-login-button">Back to Login</Button>
      </form>
    </CardContent>
  </Card>
);

const ResetForm = ({ resetToken, setResetToken, newPassword, setNewPassword, handleReset }) => (
  <Card className="card-surface border-[#27272A]" data-testid="reset-password-card">
    <CardHeader>
      <CardTitle className="text-2xl" style={{ fontFamily: 'Outfit' }}>Reset Password</CardTitle>
      <CardDescription>Enter the token from your email</CardDescription>
    </CardHeader>
    <CardContent>
      <form onSubmit={handleReset} className="space-y-4">
        <div>
          <Label htmlFor="resetToken">Reset Token</Label>
          <Input id="resetToken" value={resetToken} onChange={(e) => setResetToken(e.target.value)} required className="bg-[#0A0A0A] border-[#27272A] focus:ring-[#8B5CF6]" data-testid="reset-token-input" />
        </div>
        <div>
          <Label htmlFor="newPassword">New Password</Label>
          <Input id="newPassword" name="new-password" autoComplete="new-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required className="bg-[#0A0A0A] border-[#27272A] focus:ring-[#8B5CF6]" data-testid="new-password-input" />
        </div>
        <Button type="submit" className="btn-purple w-full rounded-full" data-testid="reset-password-button">Reset Password</Button>
      </form>
    </CardContent>
  </Card>
);

const NICKNAME_OPTIONS = ['kid', 'bud', 'champ', 'boss', 'sweetheart', 'darling', 'princess', 'honey', 'baby', 'sugar', 'love', 'dear', 'friend', 'fam', 'bro', 'sis', 'young one'];

const RegisterFormCard = ({ name, setName, nickname, setNickname, email, setEmail, password, setPassword, showPassword, setShowPassword, ageConfirmed, setAgeConfirmed, loading, handleRegister, setView }) => (
  <Card className="card-surface border-[#27272A]" data-testid="register-card">
    <CardHeader>
      <CardTitle className="text-2xl" style={{ fontFamily: 'Outfit' }}>Create Account</CardTitle>
      <CardDescription>Start your healing journey</CardDescription>
    </CardHeader>
    <CardContent>
      <form onSubmit={handleRegister} className="space-y-4">
        <div>
          <Label htmlFor="name">Name</Label>
          <Input id="name" name="name" autoComplete="given-name" value={name} onChange={(e) => setName(e.target.value)} required className="bg-[#0A0A0A] border-[#27272A] focus:ring-[#8B5CF6]" data-testid="register-name-input" />
        </div>
        <div>
          <Label htmlFor="nickname">What should we call you?</Label>
          <Input id="nickname" value={nickname} onChange={(e) => setNickname(e.target.value)} placeholder="Type your name or pick one below" className="bg-[#0A0A0A] border-[#27272A] focus:ring-[#8B5CF6] mt-1" data-testid="nickname-input" />
          <div className="flex flex-wrap gap-2 mt-2" data-testid="nickname-selector">
            {NICKNAME_OPTIONS.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setNickname(n)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${nickname === n ? 'bg-[#8B5CF6] border-[#8B5CF6] text-white' : 'bg-[#0A0A0A] border-[#27272A] text-[#A1A1AA] hover:border-[#8B5CF6]'}`}
                data-testid={`nickname-${n}`}
              >{n}</button>
            ))}
          </div>
          <p className="text-xs text-[#71717A] mt-1">You can always change this later in your profile</p>
        </div>
        <div>
          <Label htmlFor="regEmail">Email</Label>
          <EmailInput id="regEmail" value={email} onChange={(e) => setEmail(e.target.value)} dataTestId="register-email-input" autoComplete="email" />
        </div>
        <div>
          <Label htmlFor="regPassword">Password</Label>
          <div className="relative">
            <Input id="regPassword" name="new-password" autoComplete="new-password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required className="bg-[#0A0A0A] border-[#27272A] focus:ring-[#8B5CF6] pr-10" data-testid="register-password-input" />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-[#F9FAFB]">
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <p className="text-xs text-[#A1A1AA] mt-1">Min 8 chars, 1 letter, 1 number, 1 special char</p>
        </div>
        <div className="flex items-start gap-2">
          <input type="checkbox" id="ageConfirm" checked={ageConfirmed} onChange={(e) => setAgeConfirmed(e.target.checked)} className="mt-1 accent-[#8B5CF6]" data-testid="age-confirm-checkbox" />
          <label htmlFor="ageConfirm" className="text-xs text-[#A1A1AA] cursor-pointer">I confirm I am <strong className="text-[#F9FAFB]">13 years or older</strong> and agree to the <a href="/terms" target="_blank" className="text-[#8B5CF6] hover:text-[#9D6FFF] underline">Terms of Service</a></label>
        </div>
        <Button type="submit" disabled={loading || !ageConfirmed} className="btn-purple w-full rounded-full" data-testid="register-submit-button">{loading ? 'Creating your space...' : 'Come on in'}</Button>
      </form>
      <div className="mt-4 text-center">
        <span className="text-sm text-[#A1A1AA]">Already have an account? </span>
        <button onClick={() => setView('login')} className="text-sm text-[#8B5CF6] hover:text-[#9D6FFF]" data-testid="toggle-auth-mode">Sign In</button>
      </div>
    </CardContent>
  </Card>
);

const LOGIN_BUTTON_PHRASES = [
  "There you are",
  "Right where you left off",
  "Good, you're here",
  "Yep, that's me",
  "Let me in",
];

const SIGNUP_LINK_PHRASES = [
  "Come sit",
  "Pull up a chair",
  "Have a seat",
  "First time here?",
  "New here?",
  "Never done this before",
];

const pickOnce = (arr) => arr[Math.floor(Math.random() * arr.length)];

const LoginFormCard = ({ email, setEmail, password, setPassword, showPassword, setShowPassword, loading, handleLogin, setView }) => {
  const [loginPhrase] = useState(() => pickOnce(LOGIN_BUTTON_PHRASES));
  const [signupPhrase] = useState(() => pickOnce(SIGNUP_LINK_PHRASES));
  return (
  <Card className="card-surface border-[#27272A]" data-testid="login-card">
    <CardHeader>
      <CardTitle className="text-2xl" style={{ fontFamily: 'Outfit' }}>Welcome Back</CardTitle>
      <CardDescription>Sign in to continue your journey</CardDescription>
    </CardHeader>
    <CardContent>
      <form onSubmit={handleLogin} className="space-y-4">
        <div>
          <Label htmlFor="loginEmail">Email</Label>
          <EmailInput id="loginEmail" value={email} onChange={(e) => setEmail(e.target.value)} dataTestId="login-email-input" autoComplete="email" />
        </div>
        <div>
          <Label htmlFor="loginPassword">Password</Label>
          <div className="relative">
            <Input id="loginPassword" name="password" autoComplete="current-password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required className="bg-[#0A0A0A] border-[#27272A] focus:ring-[#8B5CF6] pr-10" data-testid="login-password-input" />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-[#F9FAFB]" data-testid="toggle-password-visibility">
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <Button type="submit" disabled={loading} className="btn-purple w-full rounded-full" data-testid="login-submit-button">{loading ? 'One moment...' : loginPhrase}</Button>
      </form>
      <div className="mt-4 text-center space-y-2">
        <button onClick={() => setView('forgot')} className="text-sm text-[#8B5CF6] hover:text-[#9D6FFF]" data-testid="forgot-password-link">Forget something?</button>
        <div>
          <button onClick={() => setView('register')} className="text-sm text-[#8B5CF6] hover:text-[#9D6FFF]" data-testid="toggle-auth-mode">{signupPhrase}</button>
        </div>
      </div>
    </CardContent>
  </Card>
  );
};

const FORM_MAP = {
  verify: VerifyForm,
  forgot: ForgotForm,
  reset: ResetForm,
  register: RegisterFormCard,
  login: LoginFormCard,
};

const AuthPage = () => {
  const { loginUser } = useContext(AuthContext);
  const auth = useAuthForms(loginUser);

  const FormComponent = FORM_MAP[auth.view] || LoginFormCard;

  return (
    <div className="hero-bg min-h-screen flex items-center justify-center px-4 py-8" data-testid="auth-page">
      <div className="hero-content w-full max-w-5xl flex flex-col lg:flex-row items-center gap-8">
        <DemoSlideshow />

        <div className="w-full max-w-md flex flex-col gap-4">
          <div className="bg-[#27272A] border border-[#8B5CF6] rounded-lg p-4 flex gap-3">
            <AlertCircle className="w-5 h-5 text-[#8B5CF6] flex-shrink-0 mt-0.5" />
            <div className="text-sm text-[#A1A1AA]">
              <p><strong className="text-[#F9FAFB]">Mental Health Disclaimer:</strong> This service provides AI-powered support but is not a substitute for professional mental health care.</p>
              <p className="mt-2 text-xs flex items-center gap-1.5"><span className="inline-flex items-center justify-center w-5 h-5 rounded bg-[#8B5CF6] text-white text-[10px] font-bold leading-none">13+</span>This service is intended for users aged 13 and older.</p>
            </div>
          </div>

          <FormComponent {...auth} />

          <div className="pt-2 border-t border-[#27272A]">
            <Link to="/support" className="flex items-center justify-center gap-2 text-sm text-[#D4AF37] hover:text-[#E5C04B] transition-colors" data-testid="login-support-link">
              <Heart className="w-4 h-4" />
              Support this project
            </Link>
            <p className="mt-4 text-center text-[11px] text-[#71717A] italic leading-relaxed max-w-sm mx-auto" data-testid="no-chase-promise">
              We don't send push notifications. We don't chase you. If you want to come back, you know where to find us.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
